import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { chapters } from '@/mocks/chapters';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const allCategories = categories;
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug && slug !== activeCategory) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory) || categories[0],
    [activeCategory],
  );

  const handleCategoryChange = (catSlug: string) => {
    setActiveCategory(catSlug);
    navigate(`/category/${catSlug}`, { replace: true });
  };

  const categoryChapters = useMemo(
    () => chapters.filter((ch) => ch.categoryId === currentCategory.id),
    [currentCategory.id],
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 艳母的荒唐赌约`,
    description: currentCategory.description,
    url: `https://naptevq.cn/category/${currentCategory.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '艳母的荒唐赌约',
      url: 'https://naptevq.cn/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naptevq.cn/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://naptevq.cn/category/${currentCategory.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name} - 艳母的荒唐赌约 | 章节列表`}
        description={`《艳母的荒唐赌约》${currentCategory.name}篇章，共${currentCategory.chapterCount}章。${currentCategory.description}`}
        keywords={`艳母的荒唐赌约,${currentCategory.name},章节列表,在线阅读`}
        canonicalUrl={`https://naptevq.cn/category/${currentCategory.slug}`}
        schema={{ ...categorySchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-900 font-medium">{currentCategory.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-4 overflow-x-auto flex-nowrap">
              {allCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1 opacity-70">({cat.chapterCount})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category Header */}
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0 w-full md:w-64 aspect-[4/5] rounded-xl overflow-hidden">
                <img
                  src={currentCategory.imageUrl}
                  alt={currentCategory.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 max-w-2xl">
                  {currentCategory.description}
                </p>
                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <span className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full font-medium">
                    共 {currentCategory.chapterCount} 章
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              章节列表
            </h2>
            <div className="flex flex-col gap-2">
              {categoryChapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${ch.slug}`}
                  className="group flex items-center gap-4 bg-background-100 hover:bg-background-200/70 rounded-lg p-4 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <span className="text-sm font-heading font-bold text-secondary-400">
                      {ch.chapterNumber}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {ch.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                      {ch.excerpt}
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="hidden sm:inline">{ch.publishDate}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {(ch.viewCount / 10000).toFixed(1)}万
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}